import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

function formatFollicleList(follicles) {
  return (follicles || [])
    .filter(Boolean)
    .filter(f => f.dim1 || f.dim2 || f.dim3)
    .map(f => `${[f.dim1, f.dim2, f.dim3].filter(Boolean).join(' x ')} mm`)
    .join(', ');
}

export function FollicleCountPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const ro = data.rightOvary || {};
  const lo = data.leftOvary || {};
  const endo = data.endometrium || {};

  const roVol = (ro.length && ro.width && ro.depth) ? calculateVolume(ro.length, ro.width, ro.depth) : null;
  const loVol = (lo.length && lo.width && lo.depth) ? calculateVolume(lo.length, lo.width, lo.depth) : null;

  const measurementsText = [
    (ro.length && ro.width && ro.depth)
      ? `Right ovary: ${ro.length} x ${ro.width} x ${ro.depth} cm${roVol ? ` (Vol: ${roVol} cc)` : ''}` : null,
    (ro.totalFolliclesGeq10 !== '' || ro.totalFolliclesLt10 !== '')
      ? `Right ovary AFC: ${ro.totalFolliclesGeq10 || 0} (>=10mm), ${ro.totalFolliclesLt10 || 0} (<10mm)` : null,
    (lo.length && lo.width && lo.depth)
      ? `Left ovary: ${lo.length} x ${lo.width} x ${lo.depth} cm${loVol ? ` (Vol: ${loVol} cc)` : ''}` : null,
    (lo.totalFolliclesGeq10 !== '' || lo.totalFolliclesLt10 !== '')
      ? `Left ovary AFC: ${lo.totalFolliclesGeq10 || 0} (>=10mm), ${lo.totalFolliclesLt10 || 0} (<10mm)` : null,
    endo.thickness ? `Endometrium: ${endo.thickness} mm (${endo.phase || ''})` : null,
  ].filter(Boolean).join('\n');

  const renderOvarySection = (ovary, title) => (
    <>
      <PdfAccentSectionHeader title={title} />
      {(ovary.length && ovary.width && ovary.depth) && (
        <PdfLabelValue
          label="Size:"
          value={`${ovary.length} x ${ovary.width} x ${ovary.depth} cm`}
          forceBlack
        />
      )}
      {ovary.totalFolliclesGeq10 !== '' && ovary.totalFolliclesGeq10 !== undefined && (
        <PdfLabelValue label="Follicles >=10mm:" value={String(ovary.totalFolliclesGeq10)} forceBlack />
      )}
      {formatFollicleList(ovary.folliclesGeq10) && (
        <PdfLabelValue label="  Measurements:" value={formatFollicleList(ovary.folliclesGeq10)} forceBlack />
      )}
      {ovary.totalFolliclesLt10 !== '' && ovary.totalFolliclesLt10 !== undefined && (
        <PdfLabelValue label="Follicles <10mm:" value={String(ovary.totalFolliclesLt10)} forceBlack />
      )}
      {formatFollicleList(ovary.folliclesLt10) && (
        <PdfLabelValue label="  Measurements:" value={formatFollicleList(ovary.folliclesLt10)} forceBlack />
      )}
    </>
  );

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="FOLLICLE COUNT ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {renderOvarySection(ro, 'RIGHT OVARY')}
            {renderOvarySection(lo, 'LEFT OVARY')}

            <PdfAccentSectionHeader title="ENDOMETRIUM" />
            <PdfLabelValue label="Phase:" value={endo.phase || ''} forceBlack />
            {endo.thickness && (
              <PdfLabelValue label="Thickness:" value={`${endo.thickness} mm`} forceBlack />
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
